/* eslint-disable no-nested-ternary */
import { faUser } from '@fortawesome/free-regular-svg-icons';
import { faTrash, faWarning } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { type Dispatch, type SetStateAction, useCallback, useMemo } from 'react';
import type { ShareType } from '@/graphql/types';
import { useConfirm } from '../../../confirm/Confirm';
import Select from '../../../form/Select';
import type { PERMISSION } from '../utils';
import { INVITE_STATUS, PERMISSION_TRANSLATIONS } from '../utils';
import BaseRow from './BaseRow';

import { getClient } from '../../../../utils/apollo-client';
import LoadingSpinner from '../../../loadingSpinner/LoadingSpinner';

type Share = ShareType;
interface EditRowProps {
    availablePermissions: Array<PERMISSION>;
    share: Share;
    deleteShare?: (share: Share) => void;
    pendingUpdates: Share[];
    setPendingUpdates: Dispatch<SetStateAction<Share[]>>;
}

function identify(share: Share): string | undefined {
    return getClient().cache.identify(share);
}

function EditRow({
    availablePermissions,
    share,
    deleteShare,
    pendingUpdates,
    setPendingUpdates,
}: EditRowProps): JSX.Element {
    const confirm = useConfirm();
    const isPending = useMemo(() => {
        const ref = identify(share);
        return pendingUpdates.some((pendingUpdate) => ref === identify(pendingUpdate));
    }, [share, pendingUpdates]);

    const handleDeleteClick = useCallback(() => {
        confirm({
            message: interpolate(gettext('DELETE_SHARE_CONFIRMATION'), [share.forUser!]),
            onConfirm() {
                deleteShare?.(share);
            },
        });
    }, [share, deleteShare, confirm]);

    const handleChange = useCallback(
        (e: React.ChangeEvent<HTMLSelectElement>) => {
            setPendingUpdates((current) => {
                if (e.target.value === (share.permission as string)) {
                    return current.filter(
                        (pendingUpdate) => identify(pendingUpdate) !== identify(share)
                    );
                }

                return [...current, { ...share, permission: e.target.value as PERMISSION }];
            });
        },
        [setPendingUpdates, share]
    );

    return (
        <BaseRow>
            <>
                {(share.invite === INVITE_STATUS.Pending && (
                    <LoadingSpinner className="mr-1.5 align-sub" />
                )) || <FontAwesomeIcon icon={faUser} size="sm" className="mr-2.5 align-sub" />}
                {share.forUser}
                {share.invite === INVITE_STATUS.Pending && (
                    <span className="block pl-6 pt-2">
                        <FontAwesomeIcon icon={faWarning} size="sm" className="mr-1.5 align-sub" />
                        {gettext('PENDING_SHARES_MESSAGE')}
                    </span>
                )}
            </>
            <div className="flex w-32 items-center">
                {share.sharedBy ? (
                    availablePermissions.length === 1 ? (
                        PERMISSION_TRANSLATIONS[share.permission!]
                    ) : (
                        <Select
                            small={true}
                            data={Object.entries(PERMISSION_TRANSLATIONS).filter(([key, _value]) =>
                                availablePermissions.includes(key as PERMISSION)
                            )}
                            onChange={handleChange}
                            highlight={isPending}
                            defaultValue={share.permission!}
                        />
                    )
                ) : (
                    gettext('ORIGINAL_CREATOR')
                )}
            </div>
            {share.sharedBy && (
                <button type="button" onClick={handleDeleteClick} aria-label={gettext('DELETE')}>
                    <FontAwesomeIcon icon={faTrash} className="text-red-600" size="lg" />
                </button>
            )}
        </BaseRow>
    );
}

export default EditRow;
